import {
  Box,
  useTheme,
  TextField,
  Divider,
  MenuItem,
  Checkbox,
  Typography,
  Button,
  IconButton,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import { useStateContext } from "contexts/ContextProvider";
import React, { useState } from "react";
import Grid from "@mui/material/Grid2";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import Autocomplete from "@mui/material/Autocomplete";
import { TimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import AddBoxRoundedIcon from "@mui/icons-material/AddBoxRounded";
import api from "services/api";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";

const LinguistAvailabilityEdit = () => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");

  const transformLinguistAvailabilities = (linguistAvailabilities) => {
    return linguistAvailabilities.map((availability) => ({
      id: availability.id,
      day: availability.DayOfWeek,
      startTime: availability.StartTime,
      endTime: availability.EndTime,
      availabilityTypes: [
        {
          id: 1,
          Name: "Phone",
          IsAvailable: availability.IsAvailablePhone,
        },
        {
          id: 2,
          Name: "Virtual",
          IsAvailable: availability.IsAvailableVirtual,
        },
        {
          id: 3,
          Name: "In Person",
          IsAvailable: availability.IsAvailableInPerson,
        },
      ],
    }));
  };

  const handleDayChange = (event, index) => {
    const newDay = event.target.value;
    const updatedAvailabilities = allAvailabilities.map((availability, i) =>
      i === index ? { ...availability, day: newDay } : availability,
    );
    setAllAvailabilities(updatedAvailabilities);
  };

  const handleStartTimeChange = (newTime, index) => {
    const formattedTime = newTime;
    const updatedAvailabilities = allAvailabilities.map((availability, i) =>
      i === index
        ? { ...availability, startTime: formattedTime }
        : availability,
    );
    setAllAvailabilities(updatedAvailabilities);
  };

  const handleEndTimeChange = (newTime, index) => {
    const formattedTime = newTime;
    const updatedAvailabilities = allAvailabilities.map((availability, i) =>
      i === index ? { ...availability, endTime: formattedTime } : availability,
    );
    setAllAvailabilities(updatedAvailabilities);
  };

  const handleAutocompleteChange = (event, newValue, availabilityId) => {
    setAllAvailabilities((prevAvailabilities) =>
      prevAvailabilities.map((availability) => {
        if (availability.id === availabilityId) {
          return {
            ...availability,
            availabilityTypes: availability.availabilityTypes.map((type) => ({
              ...type,
              IsAvailable: newValue.some((newType) => newType.id === type.id),
            })),
          };
        }
        return availability;
      }),
    );
  };

  const removeAvailability = (availabilityToRemove) => {
    setAllAvailabilities((prevAvailabilities) =>
      prevAvailabilities.filter(
        (availability) => availability.id !== availabilityToRemove.id,
      ),
    );
  };

  const { overlayData, setOverlayName, triggerPageRefresh } = useStateContext();

  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const [isEditable, setIsEditable] = useState(true);

  const [availability, setAvailability] = useState();
  const [note, setNote] = useState();

  const [noteError, setNoteError] = useState(false);

  const [daysOfWeek] = useState(
    overlayData.DaysOfWeek || [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ],
  );

  const [allAvailabilities, setAllAvailabilities] = useState(
    transformLinguistAvailabilities(
      overlayData.Linguist.LinguistAvailabilities,
    ) || [],
  );

  function formatTime(time) {
    const parsedTime = dayjs(time);
    return parsedTime.isValid() ? parsedTime.format("HH:mm") : time;
  }

  function saveChanges() {
    setIsSavingChanges(true);
    setIsEditable(false);

    const formattedAvailabilities = allAvailabilities.map((availability) => ({
      ...availability,
      startTime: availability.startTime
        ? formatTime(availability.startTime)
        : "",
      endTime: availability.endTime ? formatTime(availability.endTime) : "",
    }));

    const data = {
      LinguistID: overlayData.LinguistID,
      Availability: availability,
      Note: note,
      LinguistAvailabilities: formattedAvailabilities,
    };

    updateLinguistAvailabilities(data);
  }

  function updateLinguistAvailabilities(data) {
    api
      .post("/linguists/updateLinguistAvailabilities", {
        data: data,
      })
      .then((response) => {
        setIsSavingChanges(false);

        triggerPageRefresh(
          "linguist Availabilities Updated Sucessfully",
          theme,
        );

        setOverlayName("");
      })
      .catch((err) => console.log(err));
  }

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bgcolor: "rgba(0, 0, 0, 0.8)",
        zIndex: 1300,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          bgcolor: theme.palette.background.alt,
          width: "75%",
          minWidth: "600px",
          maxWidth: "1200px",
          maxHeight: "60%",
          display: "flex",
          borderRadius: "1.5rem",
          justifyContent: "center",
          alignItems: "flex-start",
          flexDirection: "column",
          color: theme.palette.text.primary,
          p: "1.5rem .2rem 1.5rem 0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            width: "100%",
            flexDirection: "row",
            p: "0 2rem",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "flex-start",
              width: "60%",
              flexDirection: "row",
            }}
          >
            <IconButton
              color="primary"
              size="large"
              onClick={() => setOverlayName("")}
            >
              <ChevronLeftRoundedIcon fontSize="inherit" />
            </IconButton>

            <Typography variant={isMobile ? "h4" : "h2"} fontWeight="bold">
              Edit Linguist's Availability
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              alignItems: "flex-end",
              width: "40%",
              flexDirection: "row",
            }}
          >
            {isMobile ? (
              <Tooltip title="Discard" placement="top">
                <IconButton
                  color="primary"
                  onClick={() => setOverlayName("")}
                  sx={{
                    width: "40px",
                    height: "40px",
                    border: `2px solid ${theme.palette.primary.main}`,
                    borderRadius: "12px",
                  }}
                >
                  <DisabledByDefaultRoundedIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                color="primary"
                variant="outlined"
                size="large"
                endIcon={<DisabledByDefaultRoundedIcon />}
                onClick={() => setOverlayName("")}
              >
                Discard
              </Button>
            )}

            <Box p="0 .5rem" />

            {isMobile ? (
              <Tooltip title="Save" placement="top">
                <IconButton
                  color="primary"
                  onClick={saveChanges}
                  sx={{
                    width: "40px",
                    height: "40px",
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.background.default,
                    borderRadius: "12px",
                    "&:hover": {
                      backgroundColor: theme.palette.primary.dark,
                    },
                  }}
                >
                  <SaveRoundedIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                loading={isSavingChanges}
                color="primary"
                variant="contained"
                size="large"
                endIcon={<SaveRoundedIcon />}
                onClick={saveChanges}
                sx={{
                  borderRadius: "12px",
                }}
              >
                Save
              </Button>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            p: "1rem 2.3rem 1rem 2.5rem",
            height: "100%",
            width: "100%",
            overflowY: "auto",
            "& .MuiTextField-root": {
              m: 0,
              width: "100%",
            },
          }}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h4" fontWeight="bold">
                Overall Availability
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                select
                id="LinguistAvailability"
                label="Linguist Availability"
                value={availability || ""}
                onChange={(event) => {
                  setAvailability(event.target.value);
                }}
              >
                {overlayData.LinguistAvailabilities.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                id="Note"
                label="Note"
                error={noteError}
                value={note || ""}
                onChange={(event) => {
                  setNote(event.target.value);
                  setNoteError(event.target.value.trim() === "");
                }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Divider />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center", // Vertically center the items
                  //justifyContent: 'space-around', // Add space between items if needed
                }}
              >
                <Typography variant="h4" fontWeight="bold" margin="0 1rem 0 0">
                  Detailed Availability
                </Typography>
                <IconButton
                  onClick={() => {
                    setAllAvailabilities((prevAvailabilities) => [
                      ...prevAvailabilities,
                      {
                        // Define the structure of an empty item here
                        id:
                          allAvailabilities.length === 0
                            ? 1
                            : allAvailabilities.at(-1).id + 1, // Example field
                        day: "",
                        startTime: "", // Example field
                        endTime: "", // Example field
                        availabilityTypes: [
                          {
                            id: 1,
                            Name: "Phone",
                            IsAvailable: false,
                          },
                          {
                            id: 2,
                            Name: "Virtual",
                            IsAvailable: false,
                          },
                          {
                            id: 3,
                            Name: "In Person",
                            IsAvailable: false,
                          },
                        ],
                      },
                    ]);
                  }}
                >
                  <AddBoxRoundedIcon
                    sx={{
                      fontSize: 24,
                      color: theme.palette.primary.main,
                    }}
                  />
                </IconButton>
              </Box>
            </Grid>
            {allAvailabilities.length === 0 ? (
              <Typography variant="body2" fontWeight="bold">
                You didn't add any availabilities for this linguist, click on
                the [+] button above to start.
              </Typography>
            ) : (
              allAvailabilities.map((singleAvailability, index) => (
                <Grid
                  key={singleAvailability.id + "--" + index}
                  container
                  size={{ xs: 12 }}
                  spacing={2}
                  bgcolor={theme.palette.background.default}
                  borderRadius="12px"
                  p=".6rem"
                >
                  <Grid size={{ xs: 12, md: 2 }}>
                    <TextField
                      disabled={!isEditable}
                      size="small"
                      select
                      id="LinguistAvailability"
                      label="Day of week"
                      error={singleAvailability.day === ""}
                      value={singleAvailability.day || ""}
                      onChange={(event) => handleDayChange(event, index)}
                    >
                      {daysOfWeek.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, md: 2 }}>
                    <TimePicker
                      disabled={!isEditable}
                      value={
                        singleAvailability.startTime
                          ? dayjs.tz(
                              singleAvailability.startTime,
                              "HH:mm:ss",
                              "America/New_York",
                            )
                          : null
                      }
                      label="Start Time"
                      onChange={(newTime) =>
                        handleStartTimeChange(newTime, index)
                      }
                      slotProps={{
                        textField: {
                          size: "small",
                          error:
                            singleAvailability.startTime === "" ||
                            singleAvailability.startTime === null,
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 2 }}>
                    <TimePicker
                      disabled={!isEditable}
                      value={
                        singleAvailability.endTime
                          ? dayjs.tz(
                              singleAvailability.endTime,
                              "HH:mm:ss",
                              "America/New_York",
                            )
                          : null
                      }
                      label="End Time"
                      onChange={(newTime) =>
                        handleEndTimeChange(newTime, index)
                      }
                      slotProps={{
                        textField: {
                          size: "small",
                          error:
                            singleAvailability.endTime === "" ||
                            singleAvailability.endTime === null,
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 5.4 }}>
                    <Autocomplete
                      disabled={!isEditable}
                      key={singleAvailability.id}
                      multiple
                      size="small"
                      options={singleAvailability.availabilityTypes}
                      disableCloseOnSelect
                      getOptionLabel={(option) => option.Name}
                      value={singleAvailability.availabilityTypes.filter(
                        (type) => type.IsAvailable,
                      )}
                      onChange={(event, newValue) =>
                        handleAutocompleteChange(
                          event,
                          newValue,
                          singleAvailability.id,
                        )
                      }
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            style={{ marginRight: 8 }}
                            checked={selected}
                          />
                          {option.Name}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Availability Types"
                          placeholder=""
                          error={
                            !singleAvailability.availabilityTypes.some(
                              (type) => type.IsAvailable,
                            )
                          }
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 0.5 }}>
                    <IconButton
                      onClick={() => removeAvailability(singleAvailability)}
                    >
                      <DeleteForeverRoundedIcon
                        sx={{
                          fontSize: 24,
                          color: theme.palette.primary.main,
                        }}
                      />
                    </IconButton>
                  </Grid>
                </Grid>
              ))
            )}
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default LinguistAvailabilityEdit;
